import { useState, useEffect } from "react";
import axios from "axios";
import AsyncSelect from "react-select/async";
import { useLocation } from "react-router-dom";
import Sidebar from "../../../components/sidebar/Sidebar";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { PackageTierSelect, PackageTierOptionsSelect, getPackageCreditRate, getPackageTier, getPackageDurationMonths, isAppServiceTier } from "../../../utils/packages";
import { PAYMENT_METHODS } from "../../../utils/paymentMethods";
import { getSelectStyles } from "../../../utils/selectStyles";
import "../../accounts/create/CreateAccountPage.css";
import { useMask } from "../../../utils/privacyMask";

export default function CreateSubscriptionPage() {
  const location = useLocation();
  const renewFrom = location.state || null;
  const { hidden } = useMask();

  const [accounts, setAccounts]           = useState([]);
  const [defaultAccount, setDefaultAccount] = useState(null);
  const [additionalAccounts, setAdditionalAccounts] = useState(renewFrom?.additionalAccounts || []);
  const [packageTier, setPackageTier]     = useState(renewFrom?.package ? getPackageTier(renewFrom.package) : "");
  const [created, setCreated]             = useState(false);
  const [loading, setLoading]             = useState(true);
  const [submitting, setSubmitting]       = useState(false);
  const [error, setError]                 = useState(null);
  const [transactionWarning, setTransactionWarning] = useState(null);
  const [addToTransactions, setAddToTransactions]   = useState(true);

  const [subscription, setSubscription] = useState({
    accountID: renewFrom?.accountID || "", username: renewFrom?.username || "", password: renewFrom?.password || "", creditCost: 0,
    package: renewFrom?.package || "", paidPrice: 0, profitReceived: 0,
    createdDate: null, expireDate: null, notes: renewFrom?.notes || "", paymentMethod: "",
  });

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/account`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      })
      .then(res => {
        setAccounts(res.data);
        if (renewFrom?.accountID) {
          const match = res.data.find(a => a._id === renewFrom.accountID);
          if (match) setDefaultAccount({ value: match._id, label: `${match.username}${match.email ? ` (${match.email})` : ''}` });
        }
        setLoading(false);
      })
      .catch(err => setError(err.response?.data?.message || "Failed to load accounts"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'createdDate' || name === 'expireDate') {
      const ms = value ? new Date(value).getTime() : null;
      if (name === 'createdDate' && ms != null && subscription.package) {
        const months = getPackageDurationMonths(subscription.package);
        if (months != null) {
          const expiry = new Date(ms);
          expiry.setMonth(expiry.getMonth() + months);
          setSubscription(prev => ({ ...prev, createdDate: ms, expireDate: expiry.getTime() }));
          return;
        }
      }
      setSubscription(prev => ({ ...prev, [name]: ms }));
    } else {
      setSubscription(prev => ({ ...prev, [name]: value }));
    }
  };

  const loadOptions = (inputValue, callback) => {
    const filtered = accounts.filter(a =>
      a.username.toLowerCase().includes(inputValue.toLowerCase()) ||
      a.email?.toLowerCase().includes(inputValue.toLowerCase())
    );
    callback(filtered.map(a => ({ value: a._id, label: `${a.username}${a.email ? ` (${a.email})` : ''}` })));
  };

  const formatDateForInput = (unixMs) => unixMs ? new Date(unixMs).toISOString().split('T')[0] : '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subscription.accountID) { setError("Please select an account"); return; }
    if (!subscription.package) { setError("Please select a package option"); return; }
    if (addToTransactions && !subscription.paymentMethod) { setError("Please select a payment method, or uncheck \"Also record this as a transaction\""); return; }
    setError(null);
    setTransactionWarning(null);
    setCreated(false);
    setSubmitting(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/subscription/create`,
        {
          ...subscription,
          creditCost: Number(subscription.creditCost),
          package: Number(subscription.package),
          paidPrice: Number(subscription.paidPrice),
          profitReceived: Number(subscription.profitReceived),
          createdDate: subscription.createdDate + "",
          expireDate: subscription.expireDate + "",
          active: 1,
          additionalAccounts,
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setCreated(true);

      if (addToTransactions) {
        try {
          await axios.post(
            `${process.env.REACT_APP_API_URL}/transaction/create`,
            {
              accountID: subscription.accountID,
              revenue: Number(subscription.paidPrice),
              profit: Number(subscription.profitReceived),
              notes: subscription.notes,
              transactionDate: subscription.createdDate + "",
              paymentMethod: subscription.paymentMethod,
            },
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
          );
        } catch (txErr) {
          setTransactionWarning(txErr.response?.data?.message || "Subscription was created, but the linked transaction could not be recorded. You may need to add it manually.");
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create subscription");
    } finally {
      setSubmitting(false);
    }
  };

  const isAppService = packageTier ? isAppServiceTier(packageTier) : false;

  // ── Cost calculation (IPTV only) ──────────────────────────────────────────
  const creditRate   = getPackageCreditRate(packageTier);
  const credits      = Number(subscription.creditCost);
  const totalCost    = creditRate != null && credits > 0 ? credits * creditRate : null;
  const revenue      = Number(subscription.paidPrice);
  const profit       = totalCost != null && revenue > 0 ? revenue - totalCost : null;

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <Sidebar title="Create Subscription" />
      <div className="form-page">
        <div className="form-panel">

          <div className="form-panel-header">
            <div>
              <div className="form-panel-title">Create New Subscription</div>
              <div className="form-panel-subtitle">Fill in the details to register a new subscription</div>
            </div>
          </div>

          <div className="form-panel-body">
            <form onSubmit={handleSubmit} noValidate>

              {/* ── Account ── */}
              <div className="form-section-label">Account</div>
              <div className="row g-4 mb-2">
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-person-fill" />Account <span className="required-star">*</span></label>
                    <AsyncSelect
                      cacheOptions
                      defaultOptions
                      loadOptions={loadOptions}
                      onChange={opt => { setDefaultAccount(opt); setSubscription(prev => ({ ...prev, accountID: opt?.value || "" })); }}
                      value={defaultAccount}
                      placeholder="Search account…"
                      isClearable
                      styles={getSelectStyles()}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-box-seam" />Package Type <span className="required-star">*</span></label>
                    <select className="form-input" value={packageTier}
                      onChange={e => { setPackageTier(e.target.value); setSubscription(prev => ({ ...prev, package: "" })); }}>
                      <PackageTierSelect />
                    </select>
                  </div>
                  {packageTier && (
                    <div className="form-field" style={{ marginTop: 10 }}>
                      <label className="form-field-label"><i className="bi bi-list-ul" />Package Option <span className="required-star">*</span></label>
                      <select className="form-input" name="package" value={subscription.package} onChange={handleChange} required>
                        <PackageTierOptionsSelect tier={packageTier} />
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {/* ── Credentials (IPTV only) ── */}
              {!isAppService && (
                <>
                  <div className="form-section-label" style={{ marginTop: 8 }}>Credentials</div>
                  <div className="row g-4 mb-2">
                    <div className="col-md-4">
                      <div className="form-field">
                        <label className="form-field-label"><i className="bi bi-person-badge" />Username <span className="required-star">*</span></label>
                        <input className="form-input" type="text" name="username" value={subscription.username} onChange={handleChange} placeholder="e.g. johndoe" required autoComplete="off" />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-field">
                        <label className="form-field-label"><i className="bi bi-key" />Password <span className="required-star">*</span></label>
                        <input className="form-input" type="text" name="password" value={subscription.password} onChange={handleChange} placeholder="e.g. pass123" required autoComplete="off" />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-field">
                        <label className="form-field-label"><i className="bi bi-coin" />Credits</label>
                        <input className="form-input" type="number" name="creditCost" value={subscription.creditCost} onChange={handleChange} min="0" />
                        {totalCost !== null && (
                          <p className="form-cost-hint">
                            <i className="bi bi-calculator" />
                            {hidden ? '••••••' : (<>{credits} credits × €{creditRate.toFixed(2)} = <span className="hint-val">€{totalCost.toFixed(2)} cost</span></>)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* ── Financials ── */}
              <div className="form-section-label" style={{ marginTop: 8 }}>Financials</div>
              <div className="row g-4 mb-2">
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-cash-stack" />Paid Price</label>
                    <div className="form-input-group">
                      <span className="form-input-prefix">€</span>
                      <input className="form-input" type="number" name="paidPrice" value={subscription.paidPrice} onChange={handleChange} step="0.01" min="0" />
                    </div>
                    {profit !== null && (
                      <p className={`form-cost-hint ${profit >= 0 ? 'hint-profit-pos' : 'hint-profit-neg'}`}>
                        <i className="bi bi-graph-up-arrow" />
                        {hidden ? '••••••' : (<>€{revenue.toFixed(2)} − €{totalCost.toFixed(2)} cost = <span className="hint-val">€{profit.toFixed(2)} profit</span></>)}
                      </p>
                    )}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-graph-up-arrow" />Profit Received</label>
                    <div className="form-input-group">
                      <span className="form-input-prefix">€</span>
                      <input className="form-input" type="number" name="profitReceived" value={subscription.profitReceived} onChange={handleChange} step="0.01" />
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-credit-card" />Payment Method{addToTransactions && <span className="required-star"> *</span>}</label>
                    <select className="form-input" name="paymentMethod" value={subscription.paymentMethod} onChange={handleChange}>
                      <option value="">Select method…</option>
                      {PAYMENT_METHODS.map(m => (
                        <option key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-6 d-flex align-items-end">
                  <label className="form-field-label" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 10 }}>
                    <input type="checkbox" checked={addToTransactions} onChange={e => setAddToTransactions(e.target.checked)} />
                    Also record this as a transaction
                  </label>
                </div>
              </div>

              {/* ── Dates ── */}
              <div className="form-section-label" style={{ marginTop: 8 }}>Dates</div>
              <div className="row g-4 mb-2">
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-calendar-plus" />Start Date <span className="required-star">*</span></label>
                    <input className="form-input" type="date" name="createdDate" value={formatDateForInput(subscription.createdDate)} onChange={handleChange} required />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-calendar-x" />Expire Date <span className="required-star">*</span></label>
                    <input className="form-input" type="date" name="expireDate" value={formatDateForInput(subscription.expireDate)} onChange={handleChange} required />
                  </div>
                </div>
              </div>

              {/* ── Additional Accounts (IPTV only) ── */}
              {!isAppService && (
                <>
                  <div className="form-section-label" style={{ marginTop: 8 }}>Additional Accounts</div>
                  <div style={{ marginBottom: 16 }}>
                    {additionalAccounts.map((acc, i) => (
                      <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                        <input className="form-input" type="text" placeholder="Username" value={acc[0]}
                          onChange={e => { const u = [...additionalAccounts]; u[i][0] = e.target.value; setAdditionalAccounts(u); }} />
                        <input className="form-input" type="text" placeholder="Password" value={acc[1]}
                          onChange={e => { const u = [...additionalAccounts]; u[i][1] = e.target.value; setAdditionalAccounts(u); }} />
                        <button type="button" onClick={() => setAdditionalAccounts(prev => prev.filter((_, idx) => idx !== i))}
                          style={{ padding: '0 12px', borderRadius: 8, border: '1px solid rgba(139,58,58,0.3)', background: 'rgba(139,58,58,0.08)', color: '#c47070', cursor: 'pointer', flexShrink: 0 }}>
                          <i className="bi bi-x-lg" />
                        </button>
                      </div>
                    ))}
                    <button type="button" onClick={() => setAdditionalAccounts(prev => [...prev, ["", ""]])}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: '1px solid rgba(79, 70, 229,0.3)', background: 'rgba(79, 70, 229,0.08)', color: '#6366F1', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}>
                      <i className="bi bi-plus-lg" /> Add Account
                    </button>
                  </div>
                </>
              )}

              {/* ── Notes ── */}
              <div className="form-section-label" style={{ marginTop: 8 }}>Notes</div>
              <div className="row g-4">
                <div className="col-12">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-journal-text" />Notes</label>
                    <textarea className="form-input" name="notes" value={subscription.notes || ''} onChange={handleChange}
                      rows={3} placeholder="Additional notes…" style={{ resize: 'vertical' }} />
                  </div>
                </div>
              </div>

              {created && <div className="form-banner success"><i className="bi bi-check-circle-fill" />Subscription created successfully!</div>}
              {transactionWarning && <div className="form-banner error"><i className="bi bi-exclamation-triangle-fill" />{transactionWarning}</div>}
              {error  && <div className="form-banner error"><i className="bi bi-exclamation-triangle-fill" />{error}</div>}

              <div className="form-actions">
                <button type="button" className="btn-form-cancel" onClick={() => window.history.back()}>
                  <i className="bi bi-x-lg" />Cancel
                </button>
                <button type="submit" className="btn-form-submit" disabled={submitting}>
                  <i className={`bi ${submitting ? 'bi-hourglass-split' : 'bi-bookmark-plus'}`} />
                  {submitting ? 'Creating…' : 'Create Subscription'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
